import { ImageUp, Trash2 } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { CAREER_LOGO_MAX_BYTES, type CareerSiteSettingsView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { ConfirmBeside } from '../../../admin/components/ConfirmBeside';
import { useChangeCareerSite } from '../../api/publishing-api';
import styles from './LogoRow.module.css';

const BYTES_PER_KB = 1024;

/**
 * The logo, in the Look group (Option A): the picture, its file name, Replace and Remove. A logo saves the moment it is
 * chosen or removed, apart from the page's Save, and the row says so. Removing can't be taken back — the file would
 * have to be uploaded again — so it asks first, beside its button (owner, 2026-11-02).
 */
export function LogoRow({ view, siteTitle, canEdit }: { readonly view: CareerSiteSettingsView; readonly siteTitle: string; readonly canEdit: boolean }): ReactNode {
  const upload = useChangeCareerSite();
  const remove = useChangeCareerSite();
  const show = useToasts((state) => state.show);
  const input = useRef<HTMLInputElement | null>(null);
  const isBusy = upload.isPending || remove.isPending;

  async function send(file: File): Promise<void> {
    if (file.size > CAREER_LOGO_MAX_BYTES) {
      show({ tone: 'danger', message: 'File is too large. Maximum 200 KB.', detail: `That file is ${Math.ceil(file.size / BYTES_PER_KB)} KB.` });
      return;
    }
    try {
      await upload.mutateAsync({ kind: 'logo', file });
      show({ tone: 'success', message: view.hasLogo ? 'Logo replaced' : 'Logo added', detail: 'It is on the site now. Visitors see it within a minute.' });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't upload the logo", detail: errorMessage(cause) });
    }
  }

  async function takeOff(): Promise<void> {
    try {
      await remove.mutateAsync({ kind: 'remove-logo' });
      show({ tone: 'success', message: 'Logo removed', detail: 'The site shows its name in its place.' });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't remove the logo", detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.logo}>
      <div className={styles.row}>
        {view.logoUrl ? (
          <img className={styles.picture} src={view.logoUrl} alt="" />
        ) : (
          <span className={styles.placeholder} aria-hidden>
            {initialOf(siteTitle)}
          </span>
        )}
        <span className={styles.facts}>
          <span className={styles.name}>{logoName(view)}</span>
          <span className={styles.sub}>PNG or JPG, up to 200 KB</span>
        </span>
        {canEdit && (
          <span className={styles.actions}>
            <input
              ref={input}
              className={styles.file}
              type="file"
              accept="image/png,image/jpeg"
              aria-label="Choose a logo file"
              tabIndex={-1}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void send(file);
              }}
            />
            <Button size="sm" variant="outline" icon={ImageUp} isPending={upload.isPending} disabled={isBusy} onClick={() => input.current?.click()}>
              {view.hasLogo ? 'Replace' : 'Upload a logo'}
            </Button>
            {view.hasLogo && (
              <ConfirmBeside
                label="Remove"
                icon={Trash2}
                variant="quiet"
                disabled={isBusy}
                isPending={remove.isPending}
                title="Remove the logo?"
                body="The site shows its name instead, straight away. To bring this logo back, upload the file again."
                confirmLabel="Remove logo"
                isDanger
                onConfirm={() => void takeOff()}
              />
            )}
          </span>
        )}
      </div>
      {canEdit && <p className={styles.note}>The logo saves as soon as you choose or remove it — it doesn't wait for Save.</p>}
    </div>
  );
}

/** The file's own name; a logo from before names were kept says only that there is one. */
function logoName(view: CareerSiteSettingsView): string {
  if (!view.hasLogo) return 'No logo — the site name shows instead';
  return view.logoFileName ?? 'Current logo';
}

function initialOf(siteTitle: string): string {
  return Array.from(siteTitle.trim())[0]?.toUpperCase() ?? '—';
}
